// ---------------------------------------------------------------------------
// report-model.ts — Builds the client-report view-model from project data,
// risk analysis output, and user-supplied options.
//
// TWO HOSTS. This module is bundled into the browser AND dynamically imported
// by the Functions host (GamePlanNodeJsFunctions/src/services/
// generate-report-service.ts) to build the project-risk.com report server-side.
// It reaches product identity through the BROWSER accessor
// (app-common/product-configuration), whose backing global
// __gameplan_product_name does not exist in Node — so inside the Functions host
// gameplanProductConfiguration() always resolves DEFAULT_PRODUCT ("GamePlan"),
// regardless of the GAMEPLAN_PRODUCT_NAME the Node accessor would read. Keep
// that in mind before adding config reads here: on the funnel they resolve to
// GamePlan, not BuilderPlan. See the same note in client-report-renderer.ts and
// executive-summary-renderer.ts, where it is the reason the funnel's branding
// strings are hardcoded.
// ---------------------------------------------------------------------------

import type {
  ClientReportModel,
  CriticalPathSummary,
  ExecutiveSummary,
  GroupSummary,
  NormalizedRisk,
  OverallRisk,
  PlanSummaryForAnalysis,
  ProjectData,
  RawRisk,
  ReportModelOptions,
  ReportState,
  RiskBand,
  ScheduleFact,
  ScheduleLabels,
} from './types';

import { convertToPlanSummary } from '../plan-summary-converter';
import { gameplanProductConfiguration } from '../../../app-common/product-configuration';
import {
  normalizeRiskLevel,
  riskLevelWeight,
  deriveRiskBand,
  compareRisks,
} from './risk-utils';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function buildClientReportModel(
  projectData: ProjectData | null,
  reportState: ReportState | null,
  options?: ReportModelOptions | null,
): ClientReportModel | null {
  if (!projectData || !reportState || !reportState.report) {
    return null;
  }

  const converted = convertToPlanSummary(projectData);
  return buildClientReportModelFromSummary(
    converted.planSummary,
    reportState,
    {
      ...options,
      fallbackProjectName: projectData.projectName,
      fallbackSourceFormat: projectData.sourceFormat,
      fallbackSourceFile: projectData.sourceFile,
    },
  );
}

/**
 * Same builder, entry point for callers that already have a PlanSummaryForAnalysis
 * (e.g. the in-app Intelligence panel, which produces one directly from the
 * in-memory scenario rather than via an ImportedProject conversion). Used by
 * the in-app Risk Report PDF download.
 */
export interface BuildFromSummaryOptions extends ReportModelOptions {
  fallbackProjectName?: string;
  fallbackSourceFormat?: string;
  fallbackSourceFile?: string;
  /**
   * In-app callers (BuilderPlan/GamePlan Intelligence panel) set this true.
   * Skips the Schedule Direction and Must Finish By fact cards (they're
   * import-only concepts), renames "Summary Tasks" → "Groups" to match the
   * in-app terminology, and falls back to schedulingStartDate when statusDate
   * isn't set on the in-memory scenario.
   */
  inAppMode?: boolean;
}

export function buildClientReportModelFromSummary(
  summary: PlanSummaryForAnalysis,
  reportState: ReportState | null,
  options?: BuildFromSummaryOptions | null,
): ClientReportModel | null {
  if (!summary || !reportState || !reportState.report) {
    return null;
  }
  const settings = options || {};
  const risks = normalizeRisks(reportState.report.risks || []);
  const topRisks = risks.slice(0, 5);
  const counts = countRiskLevels(risks);
  const overallRisk = deriveOverallRisk(risks, reportState.report.overallAssessment || '');
  const scheduleFacts = buildScheduleFacts(summary, { inAppMode: settings.inAppMode });

  return {
    version: 'client-report.v1',
    generatedAt: reportState.savedAt || new Date().toISOString(),
    project: {
      name: summary.planName || settings.fallbackProjectName || 'Untitled Project',
      sourceFormat: summary.sourceFormat || settings.fallbackSourceFormat || 'Imported Schedule',
      sourceFile: settings.sourceFile || settings.fallbackSourceFile || '',
      plannedStartDate: summary.plannedStartDate || '',
      plannedEndDate: summary.plannedEndDate || '',
      statusDate: summary.statusDate || '',
      scheduleFrom: summary.scheduleFrom || '',
      mustFinishBy: summary.mustFinishBy || '',
    },
    preparedBy: {
      name: settings.preparedByName || '',
      organization: settings.organizationName || gameplanProductConfiguration().productName,
      website: settings.organizationWebsite || '',
      email: settings.preparedByEmail || '',
      phone: settings.preparedByPhone || '',
    },
    executiveBriefText: reportState.report.executiveBrief || '',
    executiveSummary: buildExecutiveSummary(summary, overallRisk, topRisks, counts, reportState.report.overallAssessment || ''),
    overallRisk,
    topRisks,
    riskRegister: risks,
    scheduleFacts,
    methodology: buildMethodology(summary, reportState),
    appendix: buildAppendix(summary, reportState, { inAppMode: settings.inAppMode }),
  };
}

// ---------------------------------------------------------------------------
// Risk normalisation & sorting
// ---------------------------------------------------------------------------

export function normalizeRisks(risks: RawRisk[]): NormalizedRisk[] {
  return risks
    .map((risk, index) => {
      const likelihood = normalizeRiskLevel(risk.likelihood);
      const impact = normalizeRiskLevel(risk.impact);
      return {
        originalIndex: index,
        id: 'Risk ' + (index + 1),
        title: risk.title || 'Untitled risk',
        description: risk.description || '',
        likelihood,
        impact,
        affectedTasks: Array.isArray(risk.affectedTasks) ? risk.affectedTasks.filter(Boolean) : [],
        mitigation: risk.mitigation || '',
        priorityScore: riskLevelWeight(likelihood) * riskLevelWeight(impact),
      };
    })
    .sort(compareNormalizedRisks)
    .map((risk, index) => ({
      ...risk,
      id: 'Risk ' + (index + 1),
    }));
}

interface RiskBandCounts {
  high: number;
  mediumHigh: number;
  medium: number;
  mediumLow: number;
  low: number;
}

function countRiskLevels(risks: NormalizedRisk[]): RiskBandCounts {
  const counts: RiskBandCounts = { high: 0, mediumHigh: 0, medium: 0, mediumLow: 0, low: 0 };
  for (const risk of risks) {
    const derived = deriveRiskBand(risk);
    if (derived === 'high') {
      counts.high += 1;
    } else if (derived === 'medium-high') {
      counts.mediumHigh += 1;
    } else if (derived === 'medium-low') {
      counts.mediumLow += 1;
    } else if (derived === 'low') {
      counts.low += 1;
    } else {
      counts.medium += 1;
    }
  }
  return counts;
}

export function deriveOverallRisk(risks: NormalizedRisk[], assessmentText: string): OverallRisk {
  const totalScore = risks.reduce((sum, risk) => sum + risk.priorityScore, 0);
  const averageScore = risks.length ? totalScore / risks.length : 0;
  const bandCounts = countRiskLevels(risks);
  let rating: RiskBand = 'low';
  if (
    averageScore >= 7.5
    || bandCounts.high >= 3
    || (bandCounts.high >= 2 && averageScore >= 6)
  ) {
    rating = 'high';
  } else if (
    averageScore >= 6
    || bandCounts.high >= 1
    || bandCounts.mediumHigh >= 3
  ) {
    rating = 'medium-high';
  } else if (
    averageScore >= 4.5
    || bandCounts.mediumHigh >= 1
    || bandCounts.medium >= 3
  ) {
    rating = 'medium';
  } else if (averageScore >= 3 || bandCounts.mediumLow >= 1) {
    rating = 'medium-low';
  }

  return {
    rating,
    score: averageScore,
    // Use the LLM-produced assessment text when present. If the analysis
    // produced no text AND no risks, leave narrative empty — this is a
    // "structurally sound, nothing to report" outcome, and downstream
    // renderers (PDF + in-app) have clean-plan empty states that look much
    // better than this placeholder. Reserve the placeholder for the genuine
    // "we had risks but no narrative was saved" scenario, where the user
    // still needs to know something was missing.
    narrative: assessmentText
        || (risks.length > 0 ? 'No narrative assessment was saved with the latest intelligence report.' : ''),
  };
}

function compareNormalizedRisks(left: NormalizedRisk, right: NormalizedRisk): number {
  const base = compareRisks(left, right);
  if (base !== 0) return base;
  return (left.originalIndex || 0) - (right.originalIndex || 0);
}

// ---------------------------------------------------------------------------
// Executive summary
// ---------------------------------------------------------------------------

function buildExecutiveSummary(
  summary: PlanSummaryForAnalysis,
  overallRisk: OverallRisk,
  topRisks: NormalizedRisk[],
  counts: RiskBandCounts,
  assessmentText: string,
): ExecutiveSummary {
  const start = formatReportDate(summary.plannedStartDate);
  const finish = formatReportDate(summary.plannedEndDate);
  const keyPoints: string[] = [];
  const labels = getScheduleLabels(summary);
  const riskCountsText = [
    formatNumber(counts.high) + ' high-priority',
    formatNumber(counts.mediumHigh) + ' medium-high-priority',
    formatNumber(counts.medium) + ' medium-priority',
    formatNumber(counts.mediumLow) + ' medium-low-priority',
    formatNumber(counts.low) + ' low-priority',
  ].join(', ');

  keyPoints.push('The current schedule spans ' + (start || 'an unspecified start') + ' to ' + (finish || 'an unspecified finish') + '.');
  keyPoints.push('The plan includes ' + formatNumber(summary.metrics.taskCount) + ' ' + labels.activityPlural.toLowerCase() + ', ' + formatNumber(summary.metrics.milestoneCount) + ' milestones, and ' + formatNumber(summary.metrics.resourceCount) + ' resources.');
  keyPoints.push('The risk profile is assessed as ' + overallRisk.rating.toUpperCase() + ', with ' + riskCountsText + ' risks identified.');

  if (topRisks.length > 0) {
    keyPoints.push('Primary watch items are ' + topRisks.slice(0, 3).map(risk => risk.title).join('; ') + '.');
  }

  return {
    headline: overallRisk.rating === 'high'
      ? 'Active risk management is recommended to protect the delivery window.'
      : overallRisk.rating === 'medium-high'
        ? 'The schedule is workable, but elevated risks need active management.'
        : overallRisk.rating === 'medium'
          ? 'The schedule is workable, but several material risks need active monitoring.'
          : overallRisk.rating === 'medium-low'
            ? 'The schedule appears generally workable, with a limited set of issues to monitor.'
            : 'The schedule appears comparatively stable, with targeted issues to monitor.',
    // Same conditional pattern as deriveOverallRisk: emit the placeholder only
    // when risks exist but the LLM produced no text. For a clean plan with
    // zero risks, leave narrative empty so downstream clean-plan empty states
    // can render coherent prose instead of an error-flavoured placeholder.
    narrative: assessmentText
        || (topRisks.length > 0 ? 'The latest intelligence run did not include a narrative executive assessment.' : ''),
    keyPoints,
  };
}

// ---------------------------------------------------------------------------
// Schedule facts
// ---------------------------------------------------------------------------

function buildScheduleFacts(
  summary: PlanSummaryForAnalysis,
  options: { inAppMode?: boolean } = {},
): ScheduleFact[] {
  const labels = getScheduleLabels(summary);
  const criticalPaths = Array.isArray(summary.criticalPaths) ? summary.criticalPaths : [];
  const groups = Array.isArray(summary.groups) ? summary.groups : [];
  const totalTaskCount = Number(summary.metrics.taskCount || 0);
  // For in-app, derive completed/in-progress counts from the tasks array
  // directly, not from metrics.completedTaskCount. Reason: extract-plan-summary
  // runs every task / meeting / milestone through buildTaskSummary, which
  // increments the metric counter regardless of taskType, so a completed
  // milestone inflates the count without showing up in metrics.taskCount
  // (which is regularTasks.length only). Counting from summary.tasks keeps
  // tasks-only-completed aligned with the in-app workspace Summary tab.
  let completedCount: number;
  let inProgressCount: number;
  if (options.inAppMode && Array.isArray(summary.tasks)) {
    completedCount = summary.tasks.filter(t => t && t.status === 'Completed').length;
    inProgressCount = summary.tasks.filter(t => t && t.status === 'In Progress').length;
  } else {
    completedCount = Number(summary.metrics.completedTaskCount || 0);
    inProgressCount = Number(summary.metrics.inProgressTaskCount || 0);
  }
  const notStartedCount = Math.max(totalTaskCount - completedCount - inProgressCount, 0);
  const completionPercent = totalTaskCount > 0
    ? Math.round((completedCount / totalTaskCount) * 100)
    : 0;
  const longestCriticalPathLength = criticalPaths.reduce(
    (max, path) => Math.max(max, Array.isArray(path.ids) ? path.ids.length : 0),
    0,
  );
  const longestChainValue = longestCriticalPathLength > 0
    ? formatNumber(longestCriticalPathLength)
    : 'Not identified';

  if (options.inAppMode) {
    // BuilderPlan / GamePlan layout. The renderer's kpiLabels set decides
    // which entries become the blue KPI cards at the top of the page; the
    // rest flow into 3-wide detail rows in this order.
    //
    // Row 1 (KPI):  Tasks · Milestones · Task Completion · Longest Critical Chain
    // Row 2:        Planned Start · Planned Finish · Status Date
    // Row 3:        Projects · Groups · Completed Tasks
    // Row 4:        In Progress Tasks · Planned Tasks · Resources
    // Row 5:        Total Effort
    // Banner:       Critical Path Coverage
    const statusDateRaw = summary.statusDate || summary.schedulingStartDate || undefined;
    // In BuilderPlan/GamePlan, every top-level group IS a Project (= Whiteboard).
    // There's always at least one — even when the user has only opened the
    // default project the plan auto-creates and named it the same as the
    // plan. (Earlier draft excluded top-level groups whose name matched the
    // plan name, mistaking them for project-risk's plan-as-self pseudo-
    // group. That convention only exists on the project-risk MPP/P6 import
    // path; extractPlanSummaryFromScenario already drops the root group, so
    // every group in summary.groups is real.)
    const projectCount = groups.filter(g => !!g && !g.parentShortId).length;
    const nestedGroupCount = groups.filter(g => !!g && !!g.parentShortId).length;

    return [
      // KPI row (the renderer pulls these aside based on the label set,
      // regardless of where they sit in this array — but keep them up front
      // for readability).
      { label: 'Tasks', value: formatNumber(totalTaskCount) },
      { label: 'Milestones', value: formatNumber(summary.metrics.milestoneCount) },
      { label: 'Task Completion', value: completionPercent + '%' },
      { label: 'Longest Critical Chain', value: longestChainValue },

      // Detail rows
      { label: 'Planned Start', value: formatReportDate(summary.plannedStartDate) || 'Not provided' },
      { label: 'Planned Finish', value: formatReportDate(summary.plannedEndDate) || 'Not provided' },
      { label: 'Status Date', value: formatReportDate(statusDateRaw) || 'Not provided' },
      { label: 'Projects', value: formatNumber(projectCount) },
      { label: 'Groups', value: formatNumber(nestedGroupCount) },
      { label: 'Completed Tasks', value: formatNumber(completedCount) },
      { label: 'In Progress Tasks', value: formatNumber(inProgressCount) },
      { label: 'Planned Tasks', value: formatNumber(notStartedCount) },
      { label: 'Resources', value: formatNumber(summary.metrics.resourceCount) },
      { label: 'Total Effort', value: formatHours(summary.metrics.totalEffortHours) },

      // Critical Path Coverage banner
      { label: 'Critical Path Coverage', value: summarizeCriticalPaths(criticalPaths, { inAppMode: true }) },
    ];
  }

  // Project-risk MPP/P6 layout (unchanged from the original) — preserves
  // Schedule Direction / Must Finish By and the imported "Summary Tasks"
  // single-card variant.
  return [
    { label: 'Planned Start', value: formatReportDate(summary.plannedStartDate) || 'Not provided' },
    { label: 'Planned Finish', value: formatReportDate(summary.plannedEndDate) || 'Not provided' },
    { label: 'Status Date', value: formatReportDate(summary.statusDate) || 'Not provided' },
    { label: 'Schedule Direction', value: summary.scheduleFrom || 'Not provided' },
    { label: 'Must Finish By', value: formatReportDate(summary.mustFinishBy) || 'Not set' },
    { label: labels.groupMetricLabel, value: formatNumber(groups.length) },
    { label: 'Total ' + labels.activityPlural, value: formatNumber(totalTaskCount) },
    { label: 'Completed ' + labels.activityPlural, value: formatNumber(completedCount) },
    { label: 'In Progress ' + labels.activityPlural, value: formatNumber(inProgressCount) },
    { label: 'Not Started ' + labels.activityPlural, value: formatNumber(notStartedCount) },
    { label: labels.activitySingular + ' Completion', value: completionPercent + '%' },
    { label: 'Milestones', value: formatNumber(summary.metrics.milestoneCount) },
    { label: 'Resources', value: formatNumber(summary.metrics.resourceCount) },
    { label: 'Total Effort', value: formatHours(summary.metrics.totalEffortHours) },
    { label: 'Critical Path Coverage', value: summarizeCriticalPaths(criticalPaths) },
    {
      label: 'Longest Critical Chain',
      value: longestCriticalPathLength > 0
        ? formatNumber(longestCriticalPathLength) + ' ' + labels.activityPlural.toLowerCase()
        : 'Not identified',
    },
  ];
}

// ---------------------------------------------------------------------------
// Methodology & appendix
// ---------------------------------------------------------------------------

function buildMethodology(summary: PlanSummaryForAnalysis, reportState: ReportState): string[] {
  const items = [
    'This report is generated from the imported project metadata and the latest saved Intelligence-stage risk analysis output.',
    'Schedule dates are presented as schedule-local values from the imported source without browser timezone conversion.',
    'Risk prioritization is based on the structured likelihood and impact values in the saved risk register.',
    'Where scheduling inputs were available, the analysis source data uses the same resource-cleaned project view used by the Intelligence stage.',
  ];

  if (summary.statusDate) {
    items.push('The schedule status date reflected in this report is ' + formatReportDate(summary.statusDate) + '.');
  }

  if (reportState.timestampText) {
    items.push(reportState.timestampText.replace(/^Last generated:\s*/, 'The underlying intelligence report was generated on ') + '.');
  }

  return items;
}

function buildAppendix(
  summary: PlanSummaryForAnalysis,
  reportState: ReportState,
  options: { inAppMode?: boolean } = {},
): ClientReportModel['appendix'] {
  const labels = getScheduleLabels(summary);
  // In-app: BuilderPlan-native terminology — "Groups" rather than the
  // import-flavoured "Summary Tasks" / "WBS Sections".
  const groupCountLabel = options.inAppMode
    ? 'Top 10 Groups by Task Count'
    : (labels.groupCountLabel || 'Largest Schedule Sections');
  const groupItemSingular = labels.activitySingular.toLowerCase();
  const groupItemPlural = labels.activityPlural.toLowerCase();
  const groups: GroupSummary[] = Array.isArray(summary.groups)
    ? summary.groups
      .filter(group => Number(group && group.numberOfTasks) > 0)
      .filter(group => {
        if (!group) return false;
        const normalizedGroupName = String(group.name || '').trim().toLowerCase();
        const normalizedPlanName = String(summary.planName || '').trim().toLowerCase();
        // Exclude the plan-as-pseudo-group entry (no parent, name == plan).
        if (!group.parentShortId && normalizedGroupName === normalizedPlanName) return false;
        // In-app: also exclude top-level groups — those are Projects in the
        // user's mental model, counted separately in Schedule Metrics. The
        // "Top 10 Groups" list should only contain nested groups.
        if (options.inAppMode && !group.parentShortId) return false;
        return true;
      })
      .sort((left, right) => Number(right.numberOfTasks || 0) - Number(left.numberOfTasks || 0))
      .slice(0, 10)
    : [];

  return {
    groups,
    groupCountLabel,
    groupItemSingular,
    groupItemPlural,
    analysisNarrative: reportState.analysisText || '',
    reportStatus: reportState.statusText || '',
  };
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function summarizeCriticalPaths(
  criticalPaths: CriticalPathSummary[],
  options: { inAppMode?: boolean } = {},
): string {
  const paths = Array.isArray(criticalPaths) ? criticalPaths : [];
  const count = paths.length;
  if (!count) return options.inAppMode ? 'Not identified' : 'Not identified in imported data';
  // In-app: the critical paths come from the in-memory scheduler — not from
  // any "imported" source — so drop the imported/derived descriptor.
  // Project-risk imports keep the imported/derived qualifier.
  const pathName = String(paths[0] && paths[0].name || '');
  const descriptor = options.inAppMode
    ? ''
    : (pathName.startsWith('Derived ') ? 'derived ' : 'imported ');
  if (count === 1) return '1 ' + descriptor + 'critical path chain identified';
  if (count > 12) return 'Multiple ' + descriptor + 'critical path chains identified';
  return count + ' ' + descriptor + 'critical path chains identified';
}

function getScheduleLabels(summary: PlanSummaryForAnalysis): ScheduleLabels {
  const sourceFormat = String((summary && summary.sourceFormat) || '').toLowerCase();
  if (
    sourceFormat.includes('p6')
    || sourceFormat.includes('primavera')
    || sourceFormat === 'xer'
    || sourceFormat === 'pmxml'
  ) {
    return {
      activitySingular: 'Activity',
      activityPlural: 'Activities',
      groupCountLabel: 'Top 10 WBS Sections by Activity Count',
      groupMetricLabel: 'WBS Sections',
    };
  }

  return {
    activitySingular: 'Task',
    activityPlural: 'Tasks',
    groupCountLabel: 'Top 10 Summary Tasks by Task Count',
    groupMetricLabel: 'Summary Tasks',
  };
}

function formatReportDate(value: string | undefined): string {
  if (!value || typeof value !== 'string') return '';
  // Accept both ISO "T" and space-separated datetime forms — the in-app
  // scenario emits "YYYY-MM-DD HH:MM" while project-risk imports emit
  // "YYYY-MM-DDTHH:MM:SS[.ms][Z|±HH:MM]". Both should render as "Apr 27, 2026".
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::\d{2})?(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?$/);
  if (!match) return value;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  // Report-level dates are project milestones (planned start, finish, status
  // date) — the time-of-day component is noise, drop it even when the source
  // string carries one.
  return months[month - 1] + ' ' + day + ', ' + year;
}

function formatNumber(value: number): string {
  return Number(value || 0).toLocaleString();
}

function formatHours(value: number | undefined): string {
  const numeric = Number(value || 0);
  const rounded = numeric > 100
    ? Math.round(numeric)
    : Math.round(numeric * 10) / 10;
  return formatNumber(rounded) + ' hours';
}
